/**
 * @author Fernando Ruano
 * @file index.js
 * @description AppRoutes Component
 */

// Dependencies
import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { notification } from 'antd';
import { Route, Switch, Redirect, useHistory } from 'react-router-dom';

// Components
import AuthRoute from '@nephro/components/AuthRoute';

// Helpers
import {
  getStorageItem,
  setStorageItem,
  removeAllStorageItem
} from '@nephro/commons/helpers/storage-helper';

/**
 * AppRoutes Function component.
 * @param { Object } props - React PropTypes
 * @property { Array } routes - Routes to display.
 * @return { Node } React node containing app routes.
 */
const AppRoutes = ({ routes }) => {
  const history = useHistory();
  const openNotification = () => {
    caches.keys().then((keys) => {
      keys.forEach((key) => {
        return caches.delete(key);
      });
    });
    notification.info({
      message: `Sesión expirada`,
      description: 'Al parecer has estado inactivo por más de 30 minutos'
    });
  };

  // It is controlled the session time
  React.useEffect(() => {
    const lastActive = getStorageItem('lastActive');
    const userInfo = getStorageItem('user');
    const lastAuthPath = getStorageItem('lastAuthPath');
    const diffMinutes = lastActive
      ? moment.duration(moment().diff(lastActive)).asMinutes()
      : null;
    if ((lastActive === null || !diffMinutes) && userInfo) {
      setStorageItem('lastActive', moment());
    } else if (diffMinutes > 30) {
      removeAllStorageItem();
      history.replace(lastAuthPath);
      openNotification();
    }

    if (userInfo) {
      setStorageItem('lastActive', moment());
    }
  }, [window.location.pathname]);

  return (
    <Switch>
      {routes.map(
        ({ redirect, path, to, exact, component, isProtected }, key) => {
          if (isProtected) {
            return (
              <AuthRoute
                path={path}
                exact={exact}
                component={component}
                key={key}
              />
            );
          }
          return redirect ? (
            <Redirect exact={exact} from={path} to={to} key={key} />
          ) : (
            <Route path={path} exact={exact} component={component} key={key} />
          );
        }
      )}
    </Switch>
  );
};

/**
 * @name AppRoutes.propTypes
 * @type { propTypes }
 * @property { Array } routes - routes array.
 */
AppRoutes.propTypes = {
  routes: PropTypes.arrayOf(
    PropTypes.shape({
      path: PropTypes.string,
      to: PropTypes.string,
      exact: PropTypes.bool,
      redirect: PropTypes.redirect,
      component: PropTypes.any
    })
  ).isRequired
};

/**
 * Default properties.
 */
AppRoutes.defaultProps = {
  routes: []
};

export default AppRoutes;
